import React, { Component } from 'react';
import PropTypes from 'prop-types';

import FileUpload from '../Input/FileUpload.react';
import FileUploadInstructions from '../Input/FileUploadInstructions.react';
import ImgResized from '../Widgets/ImgResized.react';
import defaultImages from '../../tables/cover-images.js';

import './CoverImagePicker.scss';

export default class CoverImagePicker extends Component {
    static contextTypes = {
        confirm: PropTypes.func,
    };

    static propTypes = {
        selectedImage: PropTypes.string,
        availableImages: PropTypes.arrayOf(PropTypes.string).isRequired,
        removableImages: PropTypes.arrayOf(PropTypes.string),
        onImageSelect: PropTypes.func.isRequired,
        onImageUpload: PropTypes.func.isRequired,
        onImageRemove: PropTypes.func,
        uploadButtonText: PropTypes.string,
        uploadButtonClassName: PropTypes.string,
        uploadOption: PropTypes.bool,
        showDimensions: PropTypes.bool,
        width: PropTypes.number,
    };

    static defaultProps = {
        selectedImage: null,
        removableImages: [],
        onImageRemove: null,
        uploadButtonText: 'Upload Your Own Cover Photo',
        uploadButtonClassName: '',
        uploadOption: true,
        showDimensions: true,
        width: 225,
    };

    constructor(props) {
        super(props);

        this.state = {
            hoveredImageIndex: null,
            imageDimensions: {},
        };
    }

    componentDidMount() {
        if (this.props.showDimensions) {
            this.loadImageDimensions();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.showDimensions &&
            prevProps.availableImages !== this.props.availableImages) {
            this.loadImageDimensions();
        }
    }

    loadImageDimensions = () => {
        const { availableImages } = this.props;

        availableImages.forEach(url => {
            if (!this.state.imageDimensions[url]) {
                this.getImageDimensions(url);
            }
        });
    };

    getImageDimensions = (url) => {
        const img = new Image();
        img.onload = () => {
            this.setState(prevState => ({
                imageDimensions: {
                    ...prevState.imageDimensions,
                    [url]: {
                        width: img.naturalWidth,
                        height: img.naturalHeight,
                        aspectRatio: (img.naturalWidth / img.naturalHeight).toFixed(2)
                    }
                }
            }));
        };
        img.src = url;
    };

    handleMouseEnter = (index) => {
        this.setState({ hoveredImageIndex: index });
    };

    handleMouseLeave = () => {
        this.setState({ hoveredImageIndex: null });
    };

    handleImageSelect = (url) => {
        this.props.onImageSelect(url);
    };

    handleImageUpload = (uploadResult) => {
        const { url } = uploadResult;

        if (this.props.showDimensions) {
            this.getImageDimensions(url);
        }

        this.props.onImageUpload(uploadResult);
    };

    handleImageRemove = (url, e) => {
        const { confirm } = this.context;

        e.stopPropagation();

        if (this.props.onImageRemove) {
            confirm(
                <p>
                    Are you sure you want to remove this cover image?
                </p>,
                accept => this.props.onImageRemove(url),
                reject => false,
                { acceptText: 'Remove', rejectText: 'Keep It' }
            );
        }
    };

    renderImageDimensions = (url) => {
        if (!this.props.showDimensions) return null;

        const dimensions = this.state.imageDimensions[url];
        if (!dimensions) return null;

        const { width, height, aspectRatio } = dimensions;
        const isRecommendedRatio = Math.abs(aspectRatio - 1.75) < 0.20;

        if (isRecommendedRatio) {
            return null;
        }

        return (
            <div className="image-dimensions">
                <span className="dimensions-text">
                    {width} × {height}
                </span>
                <span
                    className={`aspect-ratio ${isRecommendedRatio ? 'recommended' : 'not-recommended'}`}
                    title={isRecommendedRatio ? 'Recommended aspect ratio' : 'Consider 1.75:1 ratio for best results'}
                >
                    {aspectRatio}:1
                </span>
            </div>
        );
    };

    renderRemoveButton = (url, index) => {
        const { removableImages, onImageRemove } = this.props;
        const { hoveredImageIndex } = this.state;

        if (!onImageRemove ||
            !removableImages.includes(url) ||
            (defaultImages && defaultImages.includes(url)) ||
            hoveredImageIndex !== index) {
            return null;
        }

        return (
            <button className="delete-btn"
                onClick={(e) => this.handleImageRemove(url, e)} title="Remove cover image">
                &times;
            </button>
        );
    };

    renderSelectedIndicator = (url) => {
        if (this.props.selectedImage !== url) return null;

        return (
            <div className="selected-indicator">
                <i className="icon-check" aria-hidden="true" />
            </div>
        );
    };

    render() {
        const {
            availableImages,
            selectedImage,
            uploadButtonText,
            uploadButtonClassName,
            width
        } = this.props;

        return (
            <div className="cover-image-picker-container">
                <ul className="cover-image-picker">
                    {availableImages.map((url, index) => {
                        const isSelected = selectedImage === url;

                        return (
                            <li
                                key={index}
                                className="cover-image-item"
                                data-selected={isSelected}
                                onClick={() => this.handleImageSelect(url)}
                                onMouseEnter={() => this.handleMouseEnter(index)}
                                onMouseLeave={this.handleMouseLeave}
                            >
                                <div className="image-container">
                                    <ImgResized src={url} width={width} />

                                    {this.renderImageDimensions(url)}
                                    {this.renderRemoveButton(url, index)}
                                    {this.renderSelectedIndicator(url)}
                                </div>
                            </li>
                        );
                    })}
                </ul>

                <hr />

                {this.props.uploadOption ? (
                    <div className="upload-section">
                        <FileUpload
                            button={uploadButtonText}
                            buttonClassName={uploadButtonClassName ?? "el-pro-raspberry-btn el-pro-medium-btn"}
                            protection="public"
                            onUploadSuccess={this.handleImageUpload}
                        />
                        <FileUploadInstructions />
                    </div>
                ) : null}
            </div>
        );
    }
}